import React, { useEffect, useState, useCallback } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table, ListGroup } from "react-bootstrap";
import { FaCheckCircle, FaClipboardList, FaFileInvoiceDollar, FaPlus, FaSearch, FaTrashAlt, FaUserEdit, FaPrint } from "react-icons/fa";
import { obtenerOrdenesCompra, crearOrdenCompra, certificarOC, obtenerProveedores, obtenerItemsCatalogo } from "../../../services/contabilidad/logisticaService";
import { generarPdfOrdenCompra } from "../../../services/contabilidad/documentosPdfService";
import { formatCurrency, formatDate } from "../../GestionContable/utils";
import { checkPermission } from "../../../functions/checkPermission";
import { logBusinessAction } from "../../../services/AuditService";


const BTN_GRADIENT = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const OrdenesCompra = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ocs, setOcs] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [newOC, setNewOC] = useState({
    proveedor_ruc: "",
    proveedor_razon_social: "",
    items: [],
    moneda: "PEN",
    observaciones: ""
  });
  const [servicioManual, setServicioManual] = useState({
    descripcion: "",
    cantidad: 1,
    precio_ref: "",
    unidad: "SERV",
  });

  const cargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      const [dataOCS, dataProv, dataCat] = await Promise.all([
        obtenerOrdenesCompra(rucEmpresa),
        obtenerProveedores(rucEmpresa),
        obtenerItemsCatalogo(rucEmpresa)
      ]);
      setOcs(dataOCS);
      setProveedores(dataProv);
      setCatalogo(dataCat);
    } catch (e) {
      setError("Error al cargar datos de logística.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleAddItem = (item) => {
    const existing = newOC.items.find(i => i.codigo === item.codigo);
    if (existing) {
       setNewOC({
         ...newOC,
         items: newOC.items.map(i => i.codigo === item.codigo ? { ...i, cantidad: i.cantidad + 1 } : i)
       });
    } else {
      setNewOC({
        ...newOC,
        items: [...newOC.items, { ...item, cantidad: 1 }]
      });
    }
  };

  const handleAddServicioManual = () => {
    const descripcion = String(servicioManual.descripcion || "").trim();
    const cantidad = Number(servicioManual.cantidad || 0);
    const precio = Number(servicioManual.precio_ref || 0);
    if (!descripcion || cantidad <= 0 || precio <= 0) {
      setError("Para agregar servicio manual debe ingresar descripción, cantidad y precio válidos.");
      return;
    }

    const item = {
      codigo: `SERV-${Date.now()}`,
      descripcion,
      cantidad,
      precio_ref: precio,
      unidad: servicioManual.unidad || "SERV",
      tipo: "SERVICIO",
    };
    setNewOC((prev) => ({ ...prev, items: [...prev.items, item] }));
    setServicioManual({ descripcion: "", cantidad: 1, precio_ref: "", unidad: "SERV" });
  };

  const handleSaveOC = async () => {
    if (!newOC.proveedor_ruc || newOC.items.length === 0) {
      setError("Debe seleccionar un proveedor y al menos un ítem.");
      return;
    }
    try {
      setSaving(true);
      const subtotal = newOC.items.reduce((acc, i) => acc + (i.cantidad * i.precio_ref), 0);
      const igv = subtotal * 0.18;
      const total = subtotal + igv;
      
      await crearOrdenCompra(rucEmpresa, {
        ...newOC,
        subtotal,
        igv,
        total,
        usuario_creador: usuario.nombre || usuario.email
      });
      
      // AUDITORÍA
      try {
        await logBusinessAction("CREACION_ORDEN_COMPRA", "GestionCompras", {
          proveedor: newOC.proveedor_razon_social,
          total: total,
          cantidadItems: newOC.items.length
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de orden de compra", e);
      }

      setSuccess("Orden de Compra generada correctamente.");
      setShowModal(false);
      await cargar();
    } catch (e) {
      setError("Error al crear la OC.");
    } finally {
      setSaving(false);
    }
  };

  const handleCertificar = async (id) => {
    if (!window.confirm("¿Está seguro de certificar (Visto Bueno) esta Orden de Compra?")) return;
    try {
      await certificarOC(id, usuario.nombre || usuario.email);
      
      // AUDITORÍA
      try {
        await logBusinessAction("APROBACION_ORDEN_COMPRA", "GestionCompras", {
          ordenCompraId: id
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de certificación de OC", e);
      }

      setSuccess("OC Certificada correctamente.");
      await cargar();
    } catch (e) {
      setError("Error al certificar OC.");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "BORRADOR": return <Badge bg="light" text="dark" className="border">BORRADOR</Badge>;
      case "APROBADO": return <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25 px-3">APROBADO (V°B°)</Badge>;
      case "CERRADO": return <Badge bg="secondary" className="px-3">CERRADO</Badge>;
      default: return <Badge bg="info">{status}</Badge>;
    }
  };

  const handleImprimirOC = (oc) => {
    try {
      generarPdfOrdenCompra({ oc, rucEmpresa });
    } catch (e) {
      setError(e?.message || "No se pudo generar el PDF de la orden de compra.");
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      {/* Resumen y Acción */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="fw-bold mb-1">Listado de Requerimientos y OCs</h5>
            <p className="small text-muted mb-0">Gestiona el ciclo de aprobación antes de facturar.</p>
          </div>
          <Button style={BTN_GRADIENT} className="rounded-pill px-4 shadow-sm" onClick={() => { 
            setNewOC({ proveedor_ruc: "", proveedor_razon_social: "", items: [], moneda: "PEN", observaciones: "" });
            setShowModal(true); 
          }}>
            <FaPlus className="me-2" /> Nueva Orden de Compra
          </Button>
        </Card.Body>
      </Card>

      {/* Tabla de OCs */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive">
          {loading ? (
             <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
          ) : (
            <Table hover className="align-middle bg-white mb-0">
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">Fecha / Correlativo</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Proveedor</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Total</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Estado</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Gestión</th>
                </tr>
              </thead>
              <tbody>
                {ocs.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-5 text-muted">No hay órdenes de compra registradas.</td></tr>
                ) : ocs.map((oc) => (
                  <tr key={oc.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td className="px-4 py-3">
                      <div className="fw-bold text-dark">{formatDate(oc.created_at?.toDate())}</div>
                      <small className="text-muted font-monospace">{oc.correlativo || oc.id}</small>
                    </td>
                    <td className="px-4 py-3">
                      <div className="fw-semibold text-dark">{oc.proveedor_razon_social}</div>
                      <small className="text-muted">{oc.proveedor_ruc}</small>
                    </td>
                    <td className="px-4 py-3 text-end fw-bold">
                      {oc.moneda} {formatCurrency(oc.total)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(oc.estado)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {oc.estado === "BORRADOR" && checkPermission(usuario, "compras", "CERTIFICAR") && (
                        <Button variant="link" className="text-success p-1 me-2" title="Dar V°B° / Certificar" onClick={() => handleCertificar(oc.id)}>
                          <FaCheckCircle size={20} />
                        </Button>
                      )}
                      <Button variant="link" className="text-dark p-1" title="Descargar PDF" onClick={() => handleImprimirOC(oc)}>
                        <FaPrint size={18} />
                      </Button>
                    </td>

                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      {/* Modal Nueva OC */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="xl">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white" }}>
          <Modal.Title className="fw-bold fs-5">
            <FaClipboardList className="me-2" /> Nueva Orden de Compra
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          <Row className="g-4">
            {/* Izquierda: Selección de Proveedor e Items */}
            <Col lg={7}>
              <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                <Card.Body>
                  <h6 className="fw-bold mb-3">1. Seleccionar Proveedor</h6>
                  <Form.Select 
                    className="border-0 bg-light mb-4"
                    value={newOC.proveedor_ruc}
                    onChange={(e) => {
                      const p = proveedores.find(x => x.ruc === e.target.value);
                      if (p) setNewOC({ ...newOC, proveedor_ruc: p.ruc, proveedor_razon_social: p.razon_social });
                    }}
                  >
                    <option value="">-- Seleccionar Proveedor del Maestro --</option>
                    {proveedores.map(p => <option key={p.ruc} value={p.ruc}>{p.razon_social} ({p.ruc})</option>)}
                  </Form.Select>

                  <h6 className="fw-bold mb-3">2. Agregar Items del Catálogo</h6>
                  <div className="position-relative mb-3">
                    <FaSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
                    <Form.Control className="ps-5 border-0 bg-light rounded-pill" placeholder="Filtrar catálogo..." />
                  </div>
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <ListGroup variant="flush">
                      {catalogo.map(item => (
                        <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center bg-transparent px-2">
                           <div>
                             <div className="fw-semibold small">{item.descripcion}</div>
                             <small className="text-muted">{item.codigo} - {formatCurrency(item.precio_ref)}</small>
                           </div>
                           <Button variant="outline-dark" size="sm" className="rounded-circle p-1" style={{ width: "28px", height: "28px" }} onClick={() => handleAddItem(item)}>
                             <FaPlus size={10} />
                           </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>

                  <hr className="my-4" />
                  <h6 className="fw-bold mb-3">3. Agregar Servicio Manual</h6>
                  <Row className="g-2">
                    <Col md={12}>
                      <Form.Control
                        className="border-0 bg-light"
                        placeholder="Ej. Servicio de control de asistencia"
                        value={servicioManual.descripcion}
                        onChange={(e) => setServicioManual((p) => ({ ...p, descripcion: e.target.value }))}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        className="border-0 bg-light"
                        placeholder="Cantidad"
                        value={servicioManual.cantidad}
                        onChange={(e) => setServicioManual((p) => ({ ...p, cantidad: e.target.value }))}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        type="number"
                        min="0.01"
                        step="0.01"
                        className="border-0 bg-light"
                        placeholder="Precio"
                        value={servicioManual.precio_ref}
                        onChange={(e) => setServicioManual((p) => ({ ...p, precio_ref: e.target.value }))}
                      />
                    </Col>
                    <Col md={4}>
                      <Button variant="outline-dark" className="w-100" onClick={handleAddServicioManual}>
                        + Agregar Servicio
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Derecha: Resumen de la OC */}
            <Col lg={5}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px", background: "#fff" }}>
                <Card.Body className="d-flex flex-column">
                  <h6 className="fw-bold mb-3">Detalle de Requerimiento</h6>
                  {newOC.items.length === 0 ? (
                    <div className="text-center py-5 text-muted opacity-50">Seleccione items para generar la OC.</div>
                  ) : (
                    <div className="flex-grow-1 overflow-auto" style={{ maxHeight: "400px" }}>
                       {newOC.items.map((i, idx) => (
                         <div key={idx} className="d-flex justify-content-between mb-3 pb-2 border-bottom align-items-center">
                            <div>
                              <div className="fw-bold small">{i.descripcion}</div>
                              <small className="text-muted">{i.cantidad} {i.unidad} @ {formatCurrency(i.precio_ref)}</small>
                            </div>
                            <div className="fw-bold small">{formatCurrency(i.cantidad * i.precio_ref)}</div>
                         </div>
                       ))}
                    </div>
                  )}

                  <div className="mt-auto pt-3 border-top">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted small">Subtotal:</span>
                      <span className="fw-semibold">{formatCurrency(newOC.items.reduce((acc, i) => acc + (i.cantidad * i.precio_ref), 0))}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-3 text-dark fs-5 fw-bold">
                      <span>Total (Inc. IGV):</span>
                      <span>{formatCurrency(newOC.items.reduce((acc, i) => acc + (i.cantidad * i.precio_ref), 0) * 1.18)}</span>
                    </div>
                    <Form.Control as="textarea" rows={2} placeholder="Observaciones / Términos de entrega..." className="border-0 bg-light small mb-3" 
                        value={newOC.observaciones} onChange={(e) => setNewOC({...newOC, observaciones: e.target.value})} />
                    <Button style={BTN_GRADIENT} className="w-100 rounded-pill py-2 shadow-sm" onClick={handleSaveOC} disabled={saving}>
                       {saving ? <Spinner size="sm" animation="border" /> : "Emitir Requerimiento / OC"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OrdenesCompra;
